'use client';

import { startTransition, useContext, useState } from 'react';
import { CaretLeft, CaretRight } from '@mezzoforte/forge-icons';
import { Box, Flex, Button, BoxProps, ButtonProps, useTokens } from '@mezzoforte/forge';
import { last } from 'lodash-es';
import { useKeydown } from '@/hooks/useKeydown';
import { ShowResponsive } from '@/components/Responsive/ShowResponsive';
import { ToggleFavoriteButton } from '@/components/Favorites/ToggleFavoriteButton';
import { useCarousel } from '@/components/ImageCarousel/useCarousel';
import { CarouselTrack } from '@/components/ImageCarousel/CarouselTrack';
import { VideoPlayerTarget } from '@/components/ImageCarousel/VideoSlide';
import OverlayBadge from '@/components/ImageCarousel/OverlayBadge';
import ImageDrawer from '@/components/ImageCarousel/ImageDrawer';
import CarouselThumbnails from '@/components/ImageCarousel/ThumbnailCarousel';
import { createEcommerceItem } from '@/util/gtm-service';
import { getManufacturer } from '@/util/metadata';
import { BiddingContext } from '@/context/BiddingContext';
import { useHermesEntry } from '@/hooks/useHermesEntry';
import { CarouselItem } from '@/components/ImageCarousel/CarouselItem';

const { shadows } = useTokens.huutokaupat;

function OverlayButton(props: ButtonProps) {
  return (
    <Button
      variant="default"
      color="text"
      zIndex="docked"
      border={props.icon && !props.children ? 'none' : undefined}
      boxShadow={shadows.box}
      _hover={{ backgroundColor: 'backgroundGrey' }}
      {...props}
    />
  );
}

export interface EntryImageCarouselProps extends BoxProps {
  entryId: number;
  items: CarouselItem[];
  isSoldToHighestBidder: boolean;
}

export function EntryImageCarousel({ entryId, items, isSoldToHighestBidder, ...boxProps }: EntryImageCarouselProps) {
  const { state, increment, decrement, jumpTo } = useCarousel(items);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const context = useContext(BiddingContext);
  const { hermesEntry } = useHermesEntry(entryId, context.vatPerc);
  const [currentVideoPlayer, setCurrentVideoPlayer] = useState<VideoPlayerTarget | null>(null);

  const exitFullscreen = () => {
    startTransition(() => {
      setIsFullscreen(false);
    });
  };
  const pauseVideo = () => {
    if (currentVideoPlayer) {
      void currentVideoPlayer.pauseVideo();
    }
  };
  const toggleFullscreen = () => {
    pauseVideo();
    startTransition(() => {
      setIsFullscreen((isFullscreen) => !isFullscreen);
    });
  };
  const enterFullscreen = () => {
    pauseVideo();
    startTransition(() => {
      setIsFullscreen(true);
    });
  };

  const shouldHideCarouselButtons = items.length < 7;
  const [imageCount, videoCount] = items.reduce(
    ([images, videos], i) => [images + (i.videoId ? 0 : 1), videos + (i.videoId ? 1 : 0)],
    [0, 0]
  );
  const firstVideoIndex = items.findIndex((i) => i.videoId !== null);
  const inputHasFocus = () => ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName ?? '');

  useKeydown('ArrowLeft', () => !inputHasFocus() && decrement());
  useKeydown('ArrowRight', () => !inputHasFocus() && increment());
  useKeydown('ArrowUp', () => isFullscreen && !inputHasFocus() && decrement());
  useKeydown('ArrowDown', () => isFullscreen && !inputHasFocus() && increment());
  useKeydown('Escape', () => exitFullscreen());

  const renderBottomControls = () => {
    if (isFullscreen) return null;

    return (
      <Flex gap={2}>
        {imageCount > 0 && (
          <OverlayButton
            flexBasis={{ base: '100%', lg: 'auto' }}
            onClick={toggleFullscreen}
            boxShadow="none"
          >
            Katso kuvat ({imageCount + videoCount} kpl)
          </OverlayButton>
        )}
        {videoCount > 0 && (
          <OverlayButton
            flexBasis={{ base: '100%', lg: 'auto' }}
            onClick={() => jumpTo(firstVideoIndex)}
            boxShadow="none"
          >
            Video ({videoCount} kpl)
          </OverlayButton>
        )}
      </Flex>
    );
  };

  const renderControls = () => (
    <>
      <Flex
        position="relative"
        gap={2}
        align="flex-start"
        justifyContent="space-between"
      >
        <ToggleFavoriteButton
          entryId={entryId}
          zIndex="docked"
          ecommerceItem={createEcommerceItem({
            id: String(context.entryId),
            name: context.title,
            company: context.companyName,
            brand: getManufacturer(context.metadata),
            category: { id: context.categoryId, name: context.categoryName },
            parentCategory:
              context.parentCategoryId !== null && context.parentCategoryName !== null
                ? { id: context.parentCategoryId, name: context.parentCategoryName }
                : undefined,
            location: last(context.location?.split(','))?.trim() ?? '',
            priceWithoutShippingOrTax: hermesEntry?.highestBid ?? context.startPrice,
          })}
          showFavoriteCount
        />
        <Flex gap={2}>
          <OverlayBadge
            backgroundColor="background"
            translate="no"
          >
            {state.currentIndex + 1} / {items.length}
          </OverlayBadge>
          {isSoldToHighestBidder && (
            <OverlayBadge backgroundColor="background">Myydään eniten tarjoavalle</OverlayBadge>
          )}
        </Flex>
      </Flex>
      <ShowResponsive sm>
        <Flex justifyContent="space-between">
          <OverlayButton
            p={0}
            onClick={decrement}
            icon={<CaretLeft size={24} />}
            aria-label="Edellinen"
          />
          <OverlayButton
            p={0}
            onClick={increment}
            icon={<CaretRight size={24} />}
            aria-label="Seuraava"
          />
        </Flex>
      </ShowResponsive>
      <Box>
        <ShowResponsive md>{renderBottomControls()}</ShowResponsive>
      </Box>
    </>
  );

  return (
    <Box {...boxProps}>
      <ImageDrawer
        items={items}
        isOpen={isFullscreen}
        onClose={exitFullscreen}
        scrollToIndex={state.currentIndex}
      />
      <Flex
        margin="0 auto"
        flexDirection="column"
        justifyContent="space-between"
        borderRadius={{ base: 0, md: '4px' }}
        overflow="hidden"
        position="relative"
        aspectRatio="4 / 3"
        mb={3}
        p={3}
        mx={{ base: -3, md: 0 }}
        cursor="pointer"
      >
        {renderControls()}
        <CarouselTrack
          state={state}
          onClick={enterFullscreen}
          increment={increment}
          decrement={decrement}
          onVideoPlay={({ target }) => setCurrentVideoPlayer(target)}
        />
      </Flex>
      <ShowResponsive mdHide>{renderBottomControls()}</ShowResponsive>
      <Box hideBelow="md">
        <CarouselThumbnails
          items={items}
          jumpTo={jumpTo}
          hideButtons={shouldHideCarouselButtons}
          currentIndex={state.currentIndex}
        />
      </Box>
    </Box>
  );
}
